<template>
  <b-overlay :show="useLoader && dataLoading" rounded="sm">
    <div v-if="filterDefinitions" class="ml-2 mt-2">
      <b-row>
        <b-col cols="12">
          <base-filter
            :filter-id="`${filterId ? filterId : entityName}`"
            :filter-types="filterDefinitions"
            :load-references-fn="filterLoadReferencesFn"
            @filterChanged="setFilter($event, true)"
            @filterIsSet="setFilter($event)"
          />
        </b-col>
      </b-row>
    </div>

    <b-table
      v-sortable-table="sortableOptions"
      :fields="columnsPrepared"
      :items="items"
      show-empty
      :empty-text="$t('common.no_records')"
      :no-local-sorting="!localSorting"
      :sort-by.sync="tableOptions.orderBy"
      :sort-desc.sync="tableOptions.isDescending"
      :selectable="selectMode != null"
      :selectMode="selectMode"
      selected-variant
      no-select-on-click
      @row-selected="$emit('update:selected', $event)"
      :thead-class="hideHeader ? 'd-none' : ''"
    >
      <template v-slot:head(select)="{ selectAllRows, clearSelected }">
        <b-check
          class="ml-1"
          @change="(checked) => (checked ? selectAllRows() : clearSelected())"
        />
      </template>
      <template v-slot:cell(select)="{ rowSelected, selectRow, unselectRow }">
        <b-check
          :checked="rowSelected"
          @change="(checked) => (checked ? selectRow() : unselectRow())"
        />
      </template>
      <template v-slot:cell(name)="{ item }">
        {{ itemNameFn(item) }}
      </template>
      <template #cell(sort)>
        <base-icon icon="grip-horizontal" size="sm" />
      </template>
      <template
        v-for="slot in Object.keys($scopedSlots)"
        :slot="slot"
        slot-scope="scope"
      >
        <slot :name="slot" v-bind="scope" />
      </template>

      <template v-slot:cell(action)="{ item }">
        <div class="text-right">
          <slot name="tableActions" :item="item" />
          <slot name="editAction" :item="item">
            <b-link
              v-if="update"
              @click.stop.prevent="$eventHub.$emit(dialogEventName, item)"
            >
              <base-icon icon="pencil" />
            </b-link>
          </slot>
          <b-link
            v-if="remove"
            class="ml-2"
            @click.stop.prevent="$eventHub.$emit(deleteConfirmEventName, item)"
          >
            <base-icon icon="trash-alt" />
          </b-link>
        </div>
      </template>
    </b-table>
    <base-dialog
      :trigger-event-name="deleteConfirmEventName"
      :title="$t('common.confirm_action')"
      :ok-title="$t('common.confirm')"
      ok-variant="danger"
      :cancel-title="$t('common.cancel')"
      :on-edit="entityDelete"
      close-on-save
      :saving="itemDeleting"
    >
      <template #default="{ editedItem }">
        {{ $t("common.confirm_delete", { name: itemNameFn(editedItem) }) }}
      </template>
    </base-dialog>

    <b-container
      v-if="
        !dataLoading &&
        tableOptions.pageSize &&
        totalCount > tableOptions.pageSize
      "
      fluid
    >
      <b-row>
        <b-col cols="auto" class="mx-2 text-left ml-auto">
          <b-pagination
            v-model="tableOptions.currentPage"
            :total-rows="totalCount"
            :per-page="tableOptions.pageSize"
          />
        </b-col>
      </b-row>
    </b-container>
  </b-overlay>
</template>

<script>
import { hashMixin, crudCommonMixin, filterMixin } from "@foys-tech/foys-utils";
import { BaseIcon, BaseDialog } from "@foys-tech/foys-base-components";

export default {
  mixins: [crudCommonMixin, filterMixin, hashMixin],
  components: { BaseIcon, BaseDialog },
  props: {
    /**
     * Enable `update` functionality - show pencil icon in the table for every row and use CrudDialog to edit entity.
     */
    update: {
      type: Boolean,
      default: true,
    },
    /**
     * Enable `delete` functionality - show trash icon in the table for every row.
     */
    remove: {
      type: Boolean,
      default: true,
    },

    /* Related to filter */

    /**
     *  `BaseFilter`. Is used to store filter presets and filter options in the local storage. Defaults to `entityName` property.
     */
    filterId: {
      type: String,
    },
    /**
     * `BaseFilter`. Filter definitions for `BaseFilter` component.
     */
    filterDefinitions: {
      type: Array,
    },
    /**
     * `BaseFilter`. Callback to load data needed for `BaseFilter` component.
     */
    filterLoadReferencesFn: null,

    /* Related to table */

    /**
     * `Table` - indicates table page size
     */
    pageSize: {
      type: Number,
    },
    /**
     * `Table` - indicates if sorting is on client side or backend side
     */
    localSorting: {
      type: Boolean,
      default: false,
    },
    /**
     * `Table` - indicates initial column to order by
     */
    orderBy: {
      type: String,
    },
    /**
     * `Table` - indicates if table is sorted descending
     */
    isDescending: {
      type: Boolean,
      default: false,
    },
    /**
     * `Table` - enable sorting on name column
     */
    sortNameCol: {
      type: Boolean,
      default: true,
    },
    /**
     * `Table` - indicates if table sort/paging options should be saved to url hash
     */
    saveSorting: {
      type: Boolean,
      default: false,
    },
    /**
     * `Table` - table columns settings
     */
    columns: {
      type: Array,
      default: function () {
        return [];
      },
    },
    /**
     * `Table` - function to process table items before rendering in table
     */
    itemsFn: {
      type: Function,
    },
    /**
     * `Table` - function to load table data
     */
    loadDataFn: {
      type: Function,
    },
    /**
     * `Table` - function to mutate default table request object (or return new)
     */
    requestParamsFn: {
      type: Function,
    },
    /**
     * `Table` - indicates if rows can be selected in the table. Selecting rows in table emits `selected` event.
     */
    selectMode: {
      type: String,
    },
    /**
     * `Table` - specify actions column width
     */
    actionsWidth: {
      type: String,
      default: "60px",
    },
    /**
     * `Table` - enable rows ordering functionality
     */
    sortable: {
      type: Boolean,
      default: false,
    },

    /**
     * Function to mutate default entity delete request object (or return new)
     */
    deleteRequestParamsFn: {
      type: Function,
    },

    useLoader: {
      type: Boolean,
      default: true,
    },

    hideHeader: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      tableOptions: {
        currentPage: 1,
        pageSize: this.pageSize,
        orderBy: this.orderBy,
        isDescending: this.isDescending,
      },
      storageKey: null,
    };
  },
  computed: {
    /* Related to table */

    columnsPrepared() {
      let columns = [...this.columns];

      if (!this.noNameProp) {
        columns.unshift({
          label: this.$t("common.name"),
          key: "name",
          sortable: this.sortNameCol,
        });
      }

      if (this.selectMode) {
        columns.unshift({
          label: "",
          key: "select",
          thStyle: "width: 30px;",
        });
      }

      if (this.sortable) {
        columns.unshift({
          label: "",
          key: "sort",
          thStyle: "width: 5px;",
          tdClass: "pr-0",
        });
      }

      if (this.update || this.remove) {
        columns.push({
          label: "",
          key: "action",
          thStyle: `width: ${this.actionsWidth};`,
        });
      }

      return columns;
    },
    items() {
      let items = this.$store.getters[`${this.ns}${this.entityName}s`];
      return this.itemsFn ? this.itemsFn(items) : items;
    },
    totalCount() {
      let items = this.$store.getters[`${this.ns}${this.entityName}s`];
      return items.totalCount;
    },
    dataLoading() {
      return (
        this.$store.getters[`${this.ns}${this.entityName}sLoading`] ||
        this.$store.getters[`${this.ns}${this.entityName}PositionSaving`]
      );
    },
    itemDeleting() {
      return this.$store.getters[`${this.ns}${this.entityName}Deleting`];
    },

    requestParams() {
      let requestParams = {
        ...this.filter,
      };
      if (!this.localSorting) {
        requestParams.orderBy = this.tableOptions.orderBy;
        requestParams.isDescending = this.tableOptions.isDescending;
      }
      if (this.tableOptions.pageSize) {
        requestParams.pageNumber = this.tableOptions.currentPage;
        requestParams.pageSize = this.tableOptions.pageSize;
      }
      if (this.requestParamsFn) {
        this.requestParamsFn(requestParams);
      }
      return requestParams;
    },
    sortableOptions() {
      return {
        enabled: this.sortable,
        chosenClass: "is-selected",
        onEnd: async (evt) => {
          let item = this.items[evt.oldIndex];
          if (!item) {
            return;
          }
          await this.$store.dispatch(
            `${this.ns}update${this.capitalizedEntityName}Position`,
            {
              params: {
                id: item.id,
              },
              data: { newIndex: evt.newIndex },
            }
          );

          await this.loadData();
        },
      };
    },

    /* Related to dialog */

    dialogEventName() {
      return `${this.eventEntityName}-dialog`;
    },
    deleteConfirmEventName() {
      return `${this.eventEntityName}-delete-confirm`;
    },
  },
  watch: {
    tableOptions: {
      deep: true,
      handler() {
        if (this.saveSorting) {
          this.saveToHash(this.storageKey, this.tableOptions);
          return;
        }
        if (!this.localSorting) {
          this.loadData();
        }
      },
    },
  },
  created() {
    this.$eventHub.$on(`${this.eventEntityName}-refresh`, this.loadData);
    this.storageKey = `table_${this.entityName}`;
    if (this.saveSorting) {
      var dataFromHash = this.getFromHash();
      if (dataFromHash && dataFromHash[this.storageKey]) {
        this.tableOptions = dataFromHash[this.storageKey];
        return;
      }
    }
    this.loadData();
  },
  beforeDestroy() {
    this.$eventHub.$off(`${this.eventEntityName}-refresh`, this.loadData);
  },
  methods: {
    itemUpdated(result) {
      this.$eventHub.$emit(`${this.eventEntityName}-updated`, result);
      this.loadData();
    },
    loadData() {
      if (this.filterDefinitions && !this.isFilterInitialized) {
        return Promise.resolve();
      }

      if (this.loadDataFn) {
        return this.loadDataFn(this.requestParams);
      } else {
        return this.$store.dispatch(
          `${this.ns}get${this.capitalizedEntityName}s`,
          {
            params: this.requestParams,
          }
        );
      }
    },
    entityDeleteRequestParams(item) {
      let params = {
        id: item.id,
      };
      if (this.deleteRequestParamsFn) {
        this.deleteRequestParamsFn(params, item);
      } else if (this.requestParamsFn) {
        this.requestParamsFn(params, item);
      }
      return params;
    },
    async entityDelete(item) {
      await this.$store.dispatch(
        `${this.ns}delete${this.capitalizedEntityName}`,
        {
          params: this.entityDeleteRequestParams(item),
        }
      );
      this.loadData();
    },
  },
};
</script>
